import React, { useMemo, memo } from 'react';
import { motion } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { Trophy, Star, Clock, ArrowRight, Link as LinkIcon, TrendingUp, Sparkles } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { DjayAvatar, DjayBubble } from '../../../mascot';
import { MODULES } from '../../../content';
import { getWeeklyActivityForLearner, getWeeklyStats } from '../../../utils/activityHelpers';
import { type LearnerAccount, type ParentAccount, type AccountsState } from '../../../accounts';
import { AppState } from '../../../app/types';
import { ParentV2Route } from '../ParentSidebarV2';
interface HomePageProps {
  accounts: AccountsState;
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
export const HomePage = memo(function HomePage({
  accounts,
  activeAccount,
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  getLearnerModuleMastery,
  setViewingLearner,
  linkParentToLearnerById,
  onNavigateToLearner,
  onNavigate
}: HomePageProps) {
  // All hooks MUST be called before any early return
  const weeklyActivity = useMemo(() => getWeeklyActivityForLearner(childData?.questionStates || {}), [childData?.questionStates]);
  const weeklyStats = useMemo(() => getWeeklyStats(childData?.questionStates || {}), [childData?.questionStates]);
  const moduleMastery = useMemo(() => {
    if (!selectedChild) return [];
    return MODULES.map(module => ({
      module,
      mastery: getLearnerModuleMastery(selectedChild.id, module.id)
    })).filter(m => m.mastery > 0).sort((a, b) => b.mastery - a.mastery);
  }, [selectedChild?.id, getLearnerModuleMastery]);
  // Empty state: No children linked — rendered AFTER all hooks
  if (linkedLearners.length === 0 || !selectedChild || !childData) {
    return <PageLayout title="Welkom!" description={`Fijn dat je er bent, ${activeAccount.displayName}`} data-id="element-4897">
        <div className="max-w-2xl mx-auto mt-12" data-id="element-4898">
          <div className="flex flex-col items-center mb-8" data-id="element-4899">
            <DjayAvatar size="lg" emotion="happy" animate data-id="element-4900" />
            <div className="mt-6 w-full max-w-sm" data-id="element-4901">
              <DjayBubble message="Hoi! Ik ben Djay. Laten we samen de voortgang van je kind volgen!" position="top" colorScheme="emerald" autoDismissMs={0} data-id="element-4902" />
            </div>
          </div>

          <Card className="p-8 border-emerald-100 shadow-sm text-center bg-white" data-id="element-4903">
            <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-4904">
              Geen kind gekoppeld
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-4905">
              Je hebt nog geen kind gekoppeld aan je account. Ga naar de
              instellingen om een kind te koppelen met een koppelcode.
            </p>

            <div className="flex justify-center" data-id="element-4906">
              <Button onClick={() => onNavigate ? onNavigate('settings') : window.location.hash = 'settings'} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl" data-id="element-4907">
                <LinkIcon className="w-4 h-4 mr-2" data-id="element-4908" />
                Naar Instellingen
              </Button>
            </div>
          </Card>
        </div>
      </PageLayout>;
  }
  // Determine Djay's message
  const getDjayMessage = () => {
    if (weeklyStats.practiceDays >= 3) {
      return {
        emotion: 'proud' as const,
        message: `Wat een topweek! ${selectedChild.displayName} heeft al ${weeklyStats.practiceDays} dagen geoefend.`
      };
    }
    if (moduleMastery.length > 0 && moduleMastery[0].mastery > 70) {
      return {
        emotion: 'happy' as const,
        message: `${selectedChild.displayName} is super goed bezig met ${moduleMastery[0].module.title.toLowerCase()}!`
      };
    }
    if (weeklyStats.practiceDays === 0) {
      return {
        emotion: 'encouraging' as const,
        message: `Tijd voor een oefensessie? 15 minuutjes per dag maakt al een groot verschil!`
      };
    }
    return {
      emotion: 'happy' as const,
      message: `Hier is het overzicht van hoe het gaat met ${selectedChild.displayName}.`
    };
  };
  const djayMessage = getDjayMessage();
  const overallMastery = moduleMastery.length > 0 ? Math.round(moduleMastery.reduce((sum, m) => sum + m.mastery, 0) / moduleMastery.length) : 0;
  return <PageLayout title="Home" description="Overzicht van de voortgang" data-id="element-4909">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-4910">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-4911" />

        {/* Hero Greeting Banner */}
        <Card className="overflow-hidden border-none shadow-md bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 relative" data-id="element-4912">
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '24px 24px'
        }} data-id="element-4913"></div>

          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6" data-id="element-4914">
            <div className="w-24 h-24 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 shadow-inner flex-shrink-0" data-id="element-4915">
              <DjayAvatar size="lg" emotion={djayMessage.emotion} data-id="element-4916" />
            </div>
            <div className="flex-1 text-center sm:text-left pt-2" data-id="element-4917">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2" data-id="element-4918">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight" data-id="element-4919">
                  Hoe gaat het met {selectedChild.displayName}?
                </h1>
                <div className="flex items-center justify-center gap-2" data-id="element-4920">
                  <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-4921">
                    <Star className="w-3 h-3 fill-current" data-id="element-4922" /> Lvl{' '}
                    {childData.stats?.level || 1}
                  </span>
                  <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-4923">
                    <Trophy className="w-3 h-3" data-id="element-4924" /> {childData.stats?.xp || 0} XP
                  </span>
                </div>
              </div>
              <p className="text-emerald-50 text-lg font-medium max-w-2xl leading-relaxed" data-id="element-4925">
                {djayMessage.message}
              </p>
            </div>
          </div>
        </Card>

        <div className="space-y-8" data-id="element-4926">
          {/* Weekly Rhythm */}
          <section data-id="element-4927">
            <h2 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-2" data-id="element-4928">
              <Clock className="w-5 h-5 text-emerald-500" data-id="element-4929" />
              Deze week
            </h2>
            <Card className="p-6 border-slate-200 shadow-sm bg-white" data-id="element-4930">
              <div className="flex flex-col md:flex-row gap-8 items-center" data-id="element-4931">
                <div className="flex-1 w-full" data-id="element-4932">
                  <div className="flex justify-between items-center mb-4 relative" data-id="element-4933">
                    {/* Connecting line */}
                    <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-slate-100 -z-10 -translate-y-1/2" data-id="element-4934"></div>

                    {weeklyActivity.map((day, i) => <div key={i} className="flex flex-col items-center gap-2 relative" data-id="element-4935">
                        <div className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-4936">
                          {day.label}
                        </div>
                        <div className="relative" data-id="element-4937">
                          {day.isToday && !day.practiced && <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-20" data-id="element-4938"></div>}
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 relative z-10 ${day.practiced ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200 scale-110' : day.isToday ? 'bg-white border-2 border-emerald-400 text-emerald-600' : 'bg-white border-2 border-slate-100 text-slate-300'}`} data-id="element-4939">
                            {day.practiced ? <span data-id="element-4940">{day.questionCount}</span> : '-'}
                          </div>
                          {day.practiced && <div className="absolute -top-2 -right-2 text-sm animate-bounce" data-id="element-4941">
                              ✨
                            </div>}
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </Card>
          </section>

          {/* Quick Navigation Cards */}
          <section data-id="element-4942">
            <h2 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-2" data-id="element-4943">
              <Sparkles className="w-5 h-5 text-emerald-500" data-id="element-4944" />
              Ontdek meer
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4945">
              <Card onClick={() => onNavigate && onNavigate('voortgang')} className="p-6 cursor-pointer hover:shadow-md transition-shadow border-slate-200 bg-white group" data-id="element-4946">
                <div className="flex justify-between items-start mb-4" data-id="element-4947">
                  <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-4948">
                    <TrendingUp className="w-6 h-6" data-id="element-4949" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-500 transition-colors" data-id="element-4950" />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-1" data-id="element-4951">
                  Voortgang
                </h3>
                <p className="text-sm text-slate-500 mb-4" data-id="element-4952">
                  Bekijk prestaties en details per vak
                </p>

                {moduleMastery.length > 0 ? <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between" data-id="element-4953">
                    <span className="text-sm font-bold text-slate-700" data-id="element-4954">
                      Gemiddelde beheersing
                    </span>
                    <span className="text-sm font-black text-emerald-600" data-id="element-4955">
                      {overallMastery}%
                    </span>
                  </div> : <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between" data-id="element-4956">
                    <span className="text-sm font-bold text-slate-700" data-id="element-4957">
                      Gemiddelde beheersing
                    </span>
                    <span className="text-sm font-black text-slate-400" data-id="element-4958">-</span>
                  </div>}
              </Card>

              <Card onClick={() => onNavigate && onNavigate('activiteit')} className="p-6 cursor-pointer hover:shadow-md transition-shadow border-slate-200 bg-white group" data-id="element-4959">
                <div className="flex justify-between items-start mb-4" data-id="element-4960">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-4961">
                    <Clock className="w-6 h-6" data-id="element-4962" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" data-id="element-4963" />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-1" data-id="element-4964">
                  Activiteit
                </h3>
                <p className="text-sm text-slate-500 mb-4" data-id="element-4965">
                  Bekijk oefenhistorie en streaks
                </p>

                <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between" data-id="element-4966">
                  <span className="text-sm font-bold text-slate-700" data-id="element-4967">
                    Oefendagen deze week
                  </span>
                  <span className="text-sm font-black text-blue-600" data-id="element-4968">
                    {weeklyStats.practiceDays} / 7
                  </span>
                </div>
              </Card>
            </div>
          </section>
        </div>
      </div>
    </PageLayout>;
});